import { useCallback, useEffect } from 'react'

import config from '@config'
import { useGigyaStore } from '@stores/gigya'
import { useParams } from '@stores/params'

export const createGigyaSDKWebUrl = (apiKey: string) => {
  return `https://cdns.us1.gigya.com/js/gigya.js?apikey=${apiKey}`
}

export const createUserProfileFromGigyaResponse = (res: any) => {
  const userProfile: Gigya.UserProfile = {
    uID: res.UID,
    uIDSignature: res.UIDSignature,
    idToken: res.id_token,
    isRegistered: res.isRegistered,
    isVerified: res.isVerified,
    loginMode: res.loginProvider,
    profile: {
      age: res.profile.age,
      birthYear: res.profile.birthYear,
      email: res.profile.email,
      firstName: res.profile.firstName,
      lastName: res.profile.lastName,
      photoURL: res.profile.photoURL,
      thumbnailURL: res.profile.thumbnailURL,
      zip: res.profile.zip,
      gender: res.profile.gender?.toUpperCase(),
    },
  }
  return userProfile
}

export const loadGigyaSDK = (apiKey: string): Promise<void> => {
  return new Promise((resolve, reject) => {
    if (document.getElementById('gigya-js')) return resolve()

    const script = document.createElement('script')
    script.id = 'gigya-js'
    script.src = createGigyaSDKWebUrl(apiKey)
    script.async = true
    script.onload = () => resolve()
    script.onerror = () => reject(new Error('Failed to load Gigya SDK'))
    document.body.appendChild(script)
  })
}

export const useLoadGigya = () => {
  const [{ retailerPartnerNumber }] = useParams()
  const [gigyaStore, setGigyaStore] = useGigyaStore()

  const loadGigya = useCallback(async () => {
    try {
      const gigyaApiKey = config.gigyaRpnApiKey[retailerPartnerNumber]
      await loadGigyaSDK(gigyaApiKey)
      setGigyaStore({ isLoaded: true })
    } catch (error) {
      console.error('Gigya sdk error:', error)
    }
  }, [retailerPartnerNumber, setGigyaStore])

  useEffect(() => {
    if (!gigyaStore.isLoaded) {
      loadGigya()
    }
  }, [gigyaStore.isLoaded, loadGigya])

  // we needs to add the event handlers to the gigya object
  // so that we can get the login and error events
  useEffect(() => {
    if (gigyaStore.isLoaded) {
      window.gigya?.accounts.addEventHandlers({
        onLogin: (res: any) => {
          //TODO: add types to onLogin
          setGigyaStore(previousStoreValue => ({
            ...previousStoreValue,
            user: createUserProfileFromGigyaResponse(res),
          }))
        },
      })
    }
  }, [gigyaStore.isLoaded, setGigyaStore])
}
